import { z } from 'zod';

export const DrawingFileStatusSchema = z.enum([
  'uploaded',
  'processing',
  'completed',
  'failed',
]);

export type DrawingFileStatus = z.infer<typeof DrawingFileStatusSchema>;

export const ConfirmedDrawingDimensionsSchema = z.object({
  length: z.number(),
  width: z.number(),
  depth: z.number(),
  units: z.string(),
  profile_edge_length: z.number().optional(),
  hole_edge_length: z.number().optional(),
});

export type ConfirmedDrawingDimensions = z.infer<
  typeof ConfirmedDrawingDimensionsSchema
>;

export const DrawingBlueprintSuggestionSchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  name: z.string(),
  confidence: z.number(),
  reason: z.string().default(''),
});

export type DrawingBlueprintSuggestion = z.infer<
  typeof DrawingBlueprintSuggestionSchema
>;

export const DrawingAdviceSchema = z.object({
  type: z.enum(['warning', 'advice']),
  text: z.string(),
});

export type DrawingAdvice = z.infer<typeof DrawingAdviceSchema>;

export type CustomQuoteTreeNode = {
  name: string;
  quantity: number;
  material_name?: string | null;
  material_id?: string | number | null;
  pricing_model_name?: string | null;
  pricing_model_id?: string | number | null;
  children: CustomQuoteTreeNode[];
};

export const CustomQuoteTreeNodeSchema: z.ZodType<CustomQuoteTreeNode> = z.lazy(
  () =>
    z.object({
      name: z.string(),
      quantity: z.number().default(1),
      material_name: z.string().nullable().optional(),
      material_id: z.union([z.string(), z.number()]).nullable().optional(),
      pricing_model_name: z.string().nullable().optional(),
      pricing_model_id: z.union([z.string(), z.number()]).nullable().optional(),
      children: z.array(CustomQuoteTreeNodeSchema).default([]),
    }),
);

export const CustomQuoteTreeSchema = z.object({
  root: CustomQuoteTreeNodeSchema.nullable(),
  confidence: z.number(),
  reason: z.string().default(''),
});

export type CustomQuoteTree = z.infer<typeof CustomQuoteTreeSchema>;

/** One indented row for the read-only Review summary. */
export type CustomQuoteTreeDisplayRow = {
  name: string;
  quantity: number;
  depth: number;
  materialLabel: string | null;
  pricingLabel: string | null;
};

export const DrawingSuggestionsSchema = z.object({
  overview: z.string().default(''),
  advice: z.array(DrawingAdviceSchema).default([]),
  blueprint: DrawingBlueprintSuggestionSchema.nullable().optional(),
  alternate_blueprints: z.array(DrawingBlueprintSuggestionSchema).default([]),
  material_match: z
    .object({
      id: z.union([z.string(), z.number()]).optional(),
      name: z.string().nullable().optional(),
      extracted_name: z.string().nullable().optional(),
      confidence: z.number(),
      reason: z.string().default(''),
    })
    .nullable()
    .optional(),
  custom_tree: CustomQuoteTreeSchema.nullable().optional(),
});

export type DrawingSuggestions = z.infer<typeof DrawingSuggestionsSchema>;

export const DrawingExtractionSchema = z.object({
  id: z.number(),
  extracted_data: z.record(z.string(), z.unknown()).default({}),
  confirmed_dimensions: ConfirmedDrawingDimensionsSchema.nullable().optional(),
  confidence: z.record(z.string(), z.unknown()).default({}),
  suggestions: DrawingSuggestionsSchema.nullable().optional(),
  complexity: z.string().nullable().optional(),
  created_at: z.string(),
});

export type DrawingExtraction = z.infer<typeof DrawingExtractionSchema>;

export const DrawingFileSchema = z.object({
  id: z.number(),
  file: z.string().nullable().optional(),
  file_url: z.string().nullable().optional(),
  filename: z.string(),
  owner: z.string(),
  company: z.number().nullable(),
  status: DrawingFileStatusSchema,
  error_message: z.string().default(''),
  created_at: z.string(),
  updated_at: z.string(),
  latest_extraction: DrawingExtractionSchema.nullable().optional(),
  extractions: z.array(DrawingExtractionSchema).optional(),
});

export type DrawingFile = z.infer<typeof DrawingFileSchema>;

export const DRAWING_UPLOAD_EXTENSIONS = ['.pdf'] as const;

export const DRAWING_UPLOAD_ACCEPT = DRAWING_UPLOAD_EXTENSIONS.join(',');

export const DRAWING_UPLOAD_EXTENSIONS_LABEL =
  DRAWING_UPLOAD_EXTENSIONS.join(', ');

export const DRAWING_SUPPORTED_UNITS = ['mm', 'cm', 'in'] as const;

export const DRAWING_COMPLEXITY_OPTIONS = [
  { value: 'simple_block', label: 'Simple Block' },
  { value: 'simple', label: 'Simple' },
  { value: 'standard', label: 'Standard' },
  { value: 'medium_high', label: 'Medium-High' },
  { value: 'highly_complex', label: 'Highly Complex' },
] as const;

export type DrawingComplexity =
  (typeof DRAWING_COMPLEXITY_OPTIONS)[number]['value'];

/** Confidence scores below this (0–1 scale) are flagged in the UI. */
export const DRAWING_LOW_CONFIDENCE_THRESHOLD = 0.5;

export const DRAWING_TRIM_EDGE_LENGTH_HELP =
  'Trim pricing requires the profile edge length and hole edge length. Enter these measurements from your drawing if you want to use a Trim pricing model. You can leave them blank and continue without Trim pricing.';

export function isLowDrawingConfidence(
  confidence: Record<string, unknown> | null | undefined,
  key: string,
): boolean {
  if (!confidence) {
    return false;
  }
  const raw = confidence[key];
  const score = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(score) && score < DRAWING_LOW_CONFIDENCE_THRESHOLD;
}

export function drawingComplexityLabel(
  value: string | null | undefined,
): string {
  if (!value) {
    return '-';
  }
  const match = DRAWING_COMPLEXITY_OPTIONS.find(
    (option) => option.value === value,
  );
  return match?.label ?? value;
}

export function isDrawingUploadFile(file: File): boolean {
  const fileName = file.name.toLowerCase();
  return DRAWING_UPLOAD_EXTENSIONS.some((ext) => fileName.endsWith(ext));
}

export function drawingUploadFileTypeError(fileNames: string[]): string {
  return `Invalid file type(s): ${fileNames.join(', ')}. Supported formats: ${DRAWING_UPLOAD_EXTENSIONS_LABEL}.`;
}

/** Local editable copy of extracted drawing fields. */
export interface DrawingExtractionEditForm {
  part_number: string;
  title: string;
  revision: string;
  material: string;
  units: string;
  length: string;
  width: string;
  depth: string;
  holes: string;
  profile_edge_length: string;
  hole_edge_length: string;
  tolerances: string;
  general_notes: string;
  complexity: DrawingComplexity | '';
  dimensions: { key: string; value: string }[];
}

export function emptyDrawingExtractionEditForm(): DrawingExtractionEditForm {
  return {
    part_number: '',
    title: '',
    revision: '',
    material: '',
    units: '',
    length: '',
    width: '',
    depth: '',
    holes: '',
    profile_edge_length: '',
    hole_edge_length: '',
    tolerances: '',
    general_notes: '',
    complexity: '',
    dimensions: [],
  };
}

export function drawingExtractionEditFormFromData(
  data: Record<string, unknown> | null | undefined,
  confirmed?: ConfirmedDrawingDimensions | null,
): DrawingExtractionEditForm {
  const form = emptyDrawingExtractionEditForm();
  if (!data) {
    return form;
  }

  form.part_number = stringValue(data['part_number']);
  form.title = stringValue(data['title']);
  form.revision = stringValue(data['revision']);
  form.material = stringValue(data['material']);
  form.units = stringValue(data['units']);
  form.tolerances = stringValue(data['tolerances']);
  form.general_notes = stringValue(data['general_notes'] ?? data['notes']);

  const holes = data['holes'];
  if (holes === null || holes === undefined) {
    form.holes = '';
  } else {
    form.holes = String(holes);
  }

  const complexity = stringValue(data['complexity']);
  if (
    DRAWING_COMPLEXITY_OPTIONS.some((option) => option.value === complexity)
  ) {
    form.complexity = complexity as DrawingComplexity;
  }

  const dimensions = data['dimensions'];
  if (
    dimensions &&
    typeof dimensions === 'object' &&
    !Array.isArray(dimensions)
  ) {
    const dimensionMap = dimensions as Record<string, unknown>;
    form.dimensions = Object.entries(dimensionMap).map(([key, value]) => ({
      key,
      value: value === null || value === undefined ? '' : String(value),
    }));
    form.length = stringValue(dimensionMap['overall_length']);
    form.width = stringValue(dimensionMap['overall_width']);
    form.depth = stringValue(dimensionMap['overall_height']);
  }

  if (confirmed) {
    form.length = String(confirmed.length);
    form.width = String(confirmed.width);
    form.depth = String(confirmed.depth);
    form.units = confirmed.units;
    if (confirmed.profile_edge_length != null) {
      form.profile_edge_length = String(confirmed.profile_edge_length);
    }
    if (confirmed.hole_edge_length != null) {
      form.hole_edge_length = String(confirmed.hole_edge_length);
    }
  }

  return form;
}

export interface DrawingConfirmPayload {
  length: number;
  width: number;
  depth: number;
  units: string;
  part_number?: string;
  title?: string;
  revision?: string;
  material?: string;
  holes?: number | null;
  profile_edge_length?: number;
  hole_edge_length?: number;
  tolerances?: string;
  general_notes?: string;
  complexity?: DrawingComplexity | null;
}

/** Confidence keys checked when deciding if a drawing tab needs review. */
export const DRAWING_REVIEW_CONFIDENCE_KEYS = [
  'part_number',
  'title',
  'revision',
  'material',
  'units',
  'dimensions',
  'holes',
  'tolerances',
  'general_notes',
  'complexity_confidence',
] as const;

/** True when length, width, depth, and units are all non-empty. */
export function drawingFormHasRequiredConfirmFields(
  form: DrawingExtractionEditForm,
): boolean {
  return (
    form.length.trim() !== '' &&
    form.width.trim() !== '' &&
    form.depth.trim() !== '' &&
    form.units.trim() !== ''
  );
}

/** True when any tracked confidence score is below the review threshold. */
export function drawingExtractionHasLowConfidence(
  confidence: Record<string, unknown> | null | undefined,
): boolean {
  return DRAWING_REVIEW_CONFIDENCE_KEYS.some((key) =>
    isLowDrawingConfidence(confidence, key),
  );
}

/**
 * Tab needs review when required confirm fields are missing or any
 * extraction confidence is low.
 */
export function drawingTabNeedsReview(
  form: DrawingExtractionEditForm,
  confidence: Record<string, unknown> | null | undefined,
): boolean {
  return (
    !drawingFormHasRequiredConfirmFields(form) ||
    drawingExtractionHasLowConfidence(confidence)
  );
}

/** Display label for a 0–1 suggestion confidence, e.g. 0.82 → "82% match". */
export function drawingSuggestionMatchLabel(confidence: number): string {
  const ratio = confidence > 1 ? confidence / 100 : confidence;
  const percent = Math.round(ratio * 100);
  return `${percent}% match`;
}

/** Comma-separated alternate blueprint names. Empty when there are none. */
export function drawingAlternateBlueprintLabel(
  alternates: { name: string }[] | null | undefined,
): string {
  return (alternates ?? [])
    .map((item) => item.name.trim())
    .filter((name) => name.length > 0)
    .join(', ');
}

/**
 * Label for a catalogue name that may or may not have resolved to an id.
 * Unmatched names are marked so Review can show them without an Apply action.
 */
export function customQuoteTreeCatalogueLabel(
  name: string | null | undefined,
  id: string | number | null | undefined,
): string | null {
  if (typeof name !== 'string') {
    return null;
  }
  const trimmed = name.trim();
  if (!trimmed) {
    return null;
  }
  if (id === null || id === undefined || id === '') {
    return `${trimmed} · not in catalogue`;
  }
  return trimmed;
}

/** Flatten a custom-tree root into indented rows for the Review summary. */
export function flattenCustomQuoteTreeForDisplay(
  root: CustomQuoteTreeNode | null | undefined,
): CustomQuoteTreeDisplayRow[] {
  if (!root || typeof root.name !== 'string' || !root.name.trim()) {
    return [];
  }

  const rows: CustomQuoteTreeDisplayRow[] = [];

  const walk = (node: CustomQuoteTreeNode, depth: number): void => {
    rows.push({
      name: node.name.trim(),
      quantity: Number.isFinite(node.quantity) ? node.quantity : 1,
      depth,
      materialLabel: customQuoteTreeCatalogueLabel(
        node.material_name,
        node.material_id,
      ),
      pricingLabel: customQuoteTreeCatalogueLabel(
        node.pricing_model_name,
        node.pricing_model_id,
      ),
    });
    for (const child of node.children ?? []) {
      if (child && typeof child.name === 'string' && child.name.trim()) {
        walk(child, depth + 1);
      }
    }
  };

  walk(root, 0);
  return rows;
}

export function customQuoteTreeHasDisplayableRoot(
  customTree: CustomQuoteTree | null | undefined,
): boolean {
  return flattenCustomQuoteTreeForDisplay(customTree?.root).length > 0;
}

export function drawingFilenameStem(filename: string): string {
  const base = filename.trim().split(/[/\\]/).pop()?.trim() || filename.trim();
  const withoutExtension = base.replace(/\.[^.]+$/, '');
  return withoutExtension || base;
}

export function buildDrawingConfirmPayload(form: DrawingExtractionEditForm): {
  payload?: DrawingConfirmPayload;
  error?: string;
} {
  const length = parseRequiredPositiveNumber(form.length, 'Length');
  if (length.error) {
    return { error: length.error };
  }
  const width = parseRequiredPositiveNumber(form.width, 'Width');
  if (width.error) {
    return { error: width.error };
  }
  const depth = parseRequiredPositiveNumber(form.depth, 'Depth');
  if (depth.error) {
    return { error: depth.error };
  }

  const units = form.units.trim().toLowerCase();
  if (!units) {
    return { error: 'Units are required.' };
  }
  const normalizedUnit = normalizeDrawingUnit(units);
  if (!normalizedUnit) {
    return {
      error: `Unsupported units "${form.units}". Use mm, cm, or in.`,
    };
  }

  let holes: number | null | undefined;
  if (form.holes.trim() !== '') {
    const parsedHoles = Number(form.holes);
    if (!Number.isFinite(parsedHoles)) {
      return { error: 'Holes must be a number when provided.' };
    }
    holes = parsedHoles;
  }

  const profileEdgeLength = parseOptionalNonNegativeNumber(
    form.profile_edge_length,
    'Profile edge length',
  );
  if (profileEdgeLength.error) {
    return { error: profileEdgeLength.error };
  }
  const holeEdgeLength = parseOptionalNonNegativeNumber(
    form.hole_edge_length,
    'Hole edge length',
  );
  if (holeEdgeLength.error) {
    return { error: holeEdgeLength.error };
  }

  const complexity =
    form.complexity &&
    DRAWING_COMPLEXITY_OPTIONS.some(
      (option) => option.value === form.complexity,
    )
      ? form.complexity
      : 'standard';

  return {
    payload: {
      length: length.value!,
      width: width.value!,
      depth: depth.value!,
      units: normalizedUnit,
      part_number: form.part_number,
      title: form.title,
      revision: form.revision,
      material: form.material,
      holes,
      ...(profileEdgeLength.value != null
        ? { profile_edge_length: profileEdgeLength.value }
        : {}),
      ...(holeEdgeLength.value != null
        ? { hole_edge_length: holeEdgeLength.value }
        : {}),
      tolerances: form.tolerances,
      general_notes: form.general_notes,
      complexity,
    },
  };
}

function normalizeDrawingUnit(units: string): string | null {
  const key = units.trim().toLowerCase();
  if (
    key === 'mm' ||
    key === 'millimeter' ||
    key === 'millimeters' ||
    key === 'millimetre' ||
    key === 'millimetres'
  ) {
    return 'mm';
  }
  if (
    key === 'cm' ||
    key === 'centimeter' ||
    key === 'centimeters' ||
    key === 'centimetre' ||
    key === 'centimetres'
  ) {
    return 'cm';
  }
  if (key === 'in' || key === 'inch' || key === 'inches' || key === '"') {
    return 'in';
  }
  return null;
}

function parseOptionalNonNegativeNumber(
  raw: string,
  label: string,
): { value?: number; error?: string } {
  if (!raw.trim()) {
    return {};
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    return { error: `${label} must be a number.` };
  }
  if (value < 0) {
    return { error: `${label} must be greater than or equal to zero.` };
  }
  return { value };
}

function parseRequiredPositiveNumber(
  raw: string,
  label: string,
): { value?: number; error?: string } {
  if (!raw.trim()) {
    return { error: `${label} is required.` };
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    return { error: `${label} must be a number.` };
  }
  if (value <= 0) {
    return { error: `${label} must be greater than zero.` };
  }
  return { value };
}

function stringValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}
